import { FormikConfig, FormikHelpers } from 'formik';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { eAlertType } from '../../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../../providers/ToastProvider/Toast.hook';
import { useUserContext } from '../../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../../providers/WorkspaceProvider/Workspace.context';
import { AGENDA_REMINDER_FIELD, DEFAULT_EMOJI } from '../Agenda.constants';
import { eFieldTypes, eUpdateType } from '../Agenda.enums';
import { AgendaService } from '../Agenda.service';
import {
  tAgendaTemplateField,
  tAgendaTemplatesContext,
  tAgendaUpdatedField,
  tManagementModalForm,
  tTemplate,
} from '../Agenda.types';
import { useCalendarContext } from '../components/Calendar/Calendar.hook';
import { TemplateFormSchema } from '../components/Templates/TemplateModal/TemplateForm.schema';
import { AgendaTemplatesContext } from '../providers/agendaTemplates.context';
import { useAgendaContext } from './agenda.hook';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../../lib/logging';

export function useAgendaTemplatesContext(): tAgendaTemplatesContext {
  return useContext(AgendaTemplatesContext);
}

export default function useAgendaTemplateView(): tAgendaTemplatesContext {
  const {
    currentEventTypes,
    setCurrentTemplates,
    currentTemplates,
    deleteTemplate,
  } = useAgendaContext();
  const { setTemplates } = useCalendarContext();
  const { currentWorkspace } = useWorkspaceContext();
  const { currentUser } = useUserContext();
  const { addAlert } = useToastContext();

  const [showTemplateModal, toggleTemplateModal] = useState<boolean>(false);
  const [isTemplateEditionMode, toggleTemplateEditionMode] =
    useState<boolean>(false);
  const [currentTemplateFields, setCurrentTemplateFields] = useState<
    Record<string, tAgendaTemplateField | null>
  >({});
  const [selectedEmoji, setSelectedEmoji] = useState<string>(
    DEFAULT_EMOJI.unified,
  );
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<tTemplate | null>(null);

  useEffect(() => {
    const templates =
      currentTemplates?.map((template) => {
        return {
          ...template,
          user_id: template.created_by,
        };
      }) ?? [];

    setTemplates(templates);
  }, [currentTemplates]);

  function openTemplateCreationModal() {
    setTemplateToEdit(null);
    setCurrentTemplateFields({});
    toggleTemplateModal(true);
    toggleTemplateEditionMode(false);
  }

  function editTemplate(template: tTemplate) {
    const fields: Record<string, tAgendaTemplateField | null> = {};

    template.fields?.forEach((field) => {
      fields[field.name] = field;
    });

    fields.reminder = template.reminder ? AGENDA_REMINDER_FIELD : null;

    setTemplateToEdit(template);
    setSelectedEmoji(template.emoji);
    setCurrentTemplateFields(fields);
    toggleTemplateEditionMode(true);
    toggleTemplateModal(true);
  }

  async function createTemplate(
    values: tManagementModalForm,
    { setSubmitting }: FormikHelpers<tManagementModalForm>,
  ) {
    if (
      isEmpty(currentWorkspace) ||
      isEmpty(currentEventTypes) ||
      isEmpty(selectedEmoji) ||
      creatingTemplate ||
      isEmpty(currentUser) ||
      isEmpty(currentTemplateFields)
    ) {
      return;
    }

    setCreatingTemplate(true);

    const fields: tAgendaTemplateField[] = Object.values(currentTemplateFields)
      .filter((field) => !isEmpty(field) && field?.name !== 'reminder')
      .map((field) => {
        return {
          name: field?.name ?? '',
          type: field?.type ?? eFieldTypes.STRING,
          required: field?.required ?? false,
        };
      });

    const template = {
      event_type: isEmpty(values.eventType)
        ? Object.values(currentEventTypes)[0].id
        : values.eventType,
      workspace_id: currentWorkspace.id,
      name: values.templateTitle,
      emoji: selectedEmoji,
      reminder: !isEmpty(currentTemplateFields?.['reminder']),
      reminder_time_minutes: !isEmpty(currentTemplateFields?.['reminder'])
        ? Number(values.reminderMinutes)
        : null,
      created_by: currentUser?.authId,
      fields: fields,
    };

    try {
      const responseTemplate =
        await AgendaService.createAgendaTemplate(template);

      if (responseTemplate) {
        const newTemplate: tTemplate = {
          ...responseTemplate.data,
        };

        setCurrentTemplates([...(currentTemplates ?? []), newTemplate]);
        toggleTemplateModal(false);
      }

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Template creado de manera correcta',
      });
    } catch (err) {
      setSubmitting(false);
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message || 'Ocurrió un error al crear el template',
      });
    } finally {
      setCreatingTemplate(false);
    }
  }

  async function updateTemplate(
    values: tManagementModalForm,
    { setSubmitting }: FormikHelpers<tManagementModalForm>,
  ) {
    if (
      isEmpty(currentWorkspace) ||
      isEmpty(currentEventTypes) ||
      isEmpty(selectedEmoji) ||
      creatingTemplate ||
      isEmpty(currentUser) ||
      isEmpty(templateToEdit) ||
      isEmpty(currentTemplateFields)
    ) {
      return;
    }

    setCreatingTemplate(true);
    const updatedFields = new Set(Object.keys(currentTemplateFields));
    const newFields: tAgendaUpdatedField[] = [];

    templateToEdit?.fields?.forEach((field) => {
      if (field.name === 'reminder') {
        return;
      }
      if (!currentTemplateFields?.[field.name]) {
        newFields.push({ ...field, update_type: eUpdateType.DELETE });
      }
      updatedFields.delete(field.name);
    });
    updatedFields?.forEach((field) => {
      if (field === 'reminder') {
        return;
      }
      if (!isEmpty(currentTemplateFields[field])) {
        newFields.push({
          ...currentTemplateFields?.[field],
          update_type: eUpdateType.CREATE,
        });
      }
    });

    const template = {
      event_type: values.eventType,
      name: values.templateTitle,
      emoji: selectedEmoji,
      reminder: !isEmpty(currentTemplateFields?.['reminder']),
      reminder_time_minutes: !isEmpty(currentTemplateFields?.['reminder'])
        ? Number(values.reminderMinutes)
        : null,
      fields: newFields,
    };

    try {
      const responseTemplate = await AgendaService.updateAgendaTemplate(
        template,
        templateToEdit.id,
      );

      if (responseTemplate) {
        const newTemplate: tTemplate = {
          ...responseTemplate.data,
        };

        setCurrentTemplates(
          currentTemplates &&
            currentTemplates.map((templates) => {
              if (templates.id === newTemplate.id) {
                return newTemplate;
              }
              return templates;
            }),
        );
        toggleTemplateModal(false);
      }
    } catch (error) {
      setSubmitting(false);
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'agendaTemplateView',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in agendaTemplateView',
        error as Error,
      );
    } finally {
      setCreatingTemplate(false);
    }
  }

  const TemplateManagementFormikOptions: FormikConfig<tManagementModalForm> = {
    initialValues: {
      templateTitle: templateToEdit?.name ?? '',
      eventType: templateToEdit?.event_type ?? '',
      reminderMinutes: templateToEdit?.reminder_time_minutes ?? 10,
    },
    validationSchema: TemplateFormSchema,
    onSubmit: isTemplateEditionMode ? updateTemplate : createTemplate,
  };

  return {
    showTemplateModal,
    toggleTemplateModal,
    isTemplateEditionMode,
    TemplateManagementFormikOptions,
    selectedEmoji,
    setSelectedEmoji,
    creatingTemplate,
    currentTemplateFields,
    deleteTemplate,
    editTemplate,
    openTemplateCreationModal,
    setCurrentTemplateFields,
  };
}
